import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Button from "../../Components/Button";
import {
  disconnectRing,
  getRingStatus,
  RingStatus,
  ringSignIn,
  ringVerifyCode,
  saveRingRefreshToken,
} from "../../services/ringService";

const KIND_LABELS = { doorbell: "Doorbell", camera: "Camera", chime: "Chime" };

/** Codes come by text, email or an authenticator app, depending on the account. */
const codePrompt = (method: string | null, sentTo: string | null) => {
  if (method === "totp") return "Enter the code from your authenticator app.";
  if (sentTo) return `Ring has sent a code to ${sentTo}.`;
  return "Ring has sent you a code by text or email.";
};

/**
 * Signs the hub in to Ring. The password goes to the Lambda once and is never
 * stored; the Lambda keeps a refresh token in DynamoDB, like Spotify's.
 */
export default function RingAuth() {
  const [status, setStatus] = useState<RingStatus | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codePromptText, setCodePromptText] = useState<string | null>(null);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [refreshToken, setRefreshToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e: any) {
      setError(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    run(async () => setStatus(await getRingStatus()));
  }, []);

  const connected = (next: RingStatus) => {
    setStatus(next);
    setPassword("");
    setCode("");
    setRefreshToken("");
    setCodePromptText(null);
    setShowTokenForm(false);
  };

  const signIn = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const result = await ringSignIn(email.trim(), password);
      setPassword("");
      if (result.needsCode) {
        setCodePromptText(codePrompt(result.method, result.sentTo));
      } else {
        connected(result);
      }
    });
  };

  const verify = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const result = await ringVerifyCode(code);
      if (!result.needsCode) connected(result);
    });
  };

  const saveToken = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => connected(await saveRingRefreshToken(refreshToken)));
  };

  const disconnect = () => {
    if (!window.confirm("Disconnect Ring from the hub?")) return;
    run(async () => {
      await disconnectRing();
      setStatus({ connected: false, devices: [] });
    });
  };

  return (
    <div className="p-4" style={{ maxWidth: 560 }}>
      {error && <div className="mb-3" style={{ color: "crimson" }}>{error}</div>}

      {status === null && !error && <p className="text-muted">Checking Ring…</p>}

      {status?.connected && (
        <>
          <div className="mb-3">
            Status: <strong>Connected</strong>
          </div>
          {status.devices.length === 0 ? (
            <p className="text-muted">No Ring devices on this account.</p>
          ) : (
            <ul className="list-group mb-3">
              {status.devices.map((device) => (
                <li
                  key={device.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>
                    {device.name}
                    <span className="text-muted"> · {KIND_LABELS[device.kind]}</span>
                  </span>
                  {device.battery !== null && (
                    <span className="text-muted">Battery {Math.round(device.battery)}%</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Button className="secondary-btn p-2" onClick={disconnect} disabled={busy}>
            Disconnect Ring
          </Button>
        </>
      )}

      {status && !status.connected && !codePromptText && !showTokenForm && (
        <Form onSubmit={signIn}>
          <p>
            Sign in with your Ring account. Your password is used once to connect
            the hub and isn't saved.
          </p>
          <Form.Group className="mb-2">
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ring email"
              autoComplete="username"
              disabled={busy}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ring password"
              autoComplete="current-password"
              disabled={busy}
              required
            />
          </Form.Group>
          <div className="d-flex gap-3 align-items-center">
            <Button type="submit" className="primary-btn p-3" disabled={busy}>
              {busy ? "Signing in…" : "Connect Ring"}
            </Button>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setShowTokenForm(true)}
            >
              Use a refresh token instead
            </button>
          </div>
        </Form>
      )}

      {codePromptText && (
        <Form onSubmit={verify}>
          <p>{codePromptText}</p>
          <Form.Group className="mb-3">
            <Form.Control
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code"
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={busy}
              required
            />
          </Form.Group>
          <div className="d-flex gap-3 align-items-center">
            <Button type="submit" className="primary-btn p-3" disabled={busy}>
              {busy ? "Checking…" : "Verify"}
            </Button>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setCodePromptText(null)}
            >
              Start again
            </button>
          </div>
        </Form>
      )}

      {showTokenForm && (
        <Form onSubmit={saveToken}>
          <p>
            If signing in here is blocked, make a refresh token on a computer with{" "}
            <code>npx -p @tsightler/ring-client-api ring-auth-cli</code> and paste it below.
          </p>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={4}
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              placeholder="Refresh token"
              disabled={busy}
              required
            />
          </Form.Group>
          <div className="d-flex gap-3 align-items-center">
            <Button type="submit" className="primary-btn p-3" disabled={busy}>
              {busy ? "Checking…" : "Save token"}
            </Button>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setShowTokenForm(false)}
            >
              Back
            </button>
          </div>
        </Form>
      )}
    </div>
  );
}
